import { AuthFetchWithResponse } from '@e3dc-react/shell/Types/api'
import { CompaniesFetchModel, CompanyForm, CompanyModel } from '../models/company.model'
import { CompanyDto } from './dto/company.dto'
import { StatusCodes } from '@e3dc-react/shell/Libs'
import { handleFetchError } from 'src/Libs/api/utils'
import { ApiResult } from 'src/Libs/api'
import companyDtoToModelTransformer from './transformer/companydto-to-model.transfromer'
import { objectApiResult } from '@e3dc-react/shell/Libs/ApiHelpers'
import { CompanyLocationDto } from 'src/Libs/user/api/dto/user.dto'
import { CompanyContactData } from 'src/Libs/user/models'
import fromCompanyDtoToCompanyContactDataModelTransformer from 'src/Libs/user/transformers/from-company-dto-to-company-contact-data-model.transformer'
import { Payload } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { CompanyConnectedModel } from '../models/company-connected.model'
import { CompanyConnectedDto } from './dto/company-connected.dto'
import companyConnectedDtoToModelTransformer from './transformer/company-connected-dto-to-model.transfromer'
import { companyFormModelToDtoTransformer } from './transformer/companyform-model-to-dto.transformer'
import { isFlowTheme } from 'src/Libs/utils'

/**
 * Fetch a company by the given ID
 * @param companyId companyId of the company that should be fetched
 * @returns a company
 */
export const fetchCompany = async (companyId: number | string | undefined, authFetch: AuthFetchWithResponse): Promise<ApiResult<CompanyModel>> => {
  if (!companyId || Number.isNaN(Number(companyId)) || isFlowTheme) {
    return new ApiResult<CompanyModel>(null, true, new Error('No company ID provided'))
  }

  try {
    const response = await authFetch<CompanyDto>({
      path: `users/companies/${companyId}`,
      method: 'GET',
    })
    if (response.status === StatusCodes.OK) {
      const model = companyDtoToModelTransformer(response.data)
      return new ApiResult<CompanyModel>(model, false)
    }
    return new ApiResult<CompanyModel>(null, true)
  } catch (error) {
    return handleFetchError<CompanyModel>(error)
  }
}

/**
 * Update a company with the given values
 * @param companyId companyId of the company wich will be updated
 * @param values the values wich should be updated
 */
export const updateCompany = async (companyId: number, values: CompanyForm, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/companies/${companyId}`,
      method: 'PUT',
      payload: companyFormModelToDtoTransformer(values),
    })
    if (response.status !== StatusCodes.OK) {
      return new ApiResult<void>(null, true)
    }
    return new ApiResult<void>(response.data, false)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

/**
 * create a new company
 * @param company values for the company
 */
export const createCompany = async (company: CompanyForm, authFetch: AuthFetchWithResponse): Promise<ApiResult<number | null>> => {
  try {
    const response = await authFetch<number | null>({
      path: `users/companies`,
      method: 'POST',
      payload: companyFormModelToDtoTransformer(company),
    })

    if (response.status !== StatusCodes.CREATED) {
      return new ApiResult<number | null>(null, true)
    }

    return new ApiResult<number | null>(response.data, false)
  } catch (error) {
    return handleFetchError<number | null>(error)
  }
}

/**
 * Update the showroom property of a company
 * @param companyId companyId of the company wich will be updated
 * @param value the new value for showroom
 */
export const updateCompanySettings = async (companyId: number, value: boolean, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/companies/${companyId}/settings`,
      method: 'PUT',
      payload: { showroom: value },
    })

    if (response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, true)
    }
    return new ApiResult<void>(response.data, false)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

/**
 *
 * Fetch all available companies
 * @returns a list of companies
 */
export const fetchAllCompanies = async (authFetch: AuthFetchWithResponse): Promise<ApiResult<CompanyModel[]>> => {
  try {
    const response = await authFetch<CompaniesFetchModel>({
      path: `users/companies`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const responseAsArray = objectApiResult({ ...response.data })
      const model = responseAsArray.map(company => companyDtoToModelTransformer(company))
      return new ApiResult<CompanyModel[]>(model, false)
    }

    return new ApiResult<CompanyModel[]>([], true)
  } catch (error) {
    return handleFetchError<CompanyModel[]>(error)
  }
}

/**
 *
 * Fetch all available companies with additional location and/or manager info.
 * @returns a list of companies
 */
export const fetchAllCompaniesConnected = async (
  authFetch: AuthFetchWithResponse,
  payload?: Payload
): Promise<ApiResult<CompanyConnectedModel[]>> => {
  try {
    /** In case of different response of route with connect query and without, the connect query must be defined. */
    const requestPayload: Payload = payload?.connect ? payload : { ...(payload ?? {}), connect: 'location,manager' }

    const response = await authFetch<CompanyConnectedDto>({
      path: `users/companies`,
      method: 'GET',
      payload: requestPayload,
    })

    if (response.status === StatusCodes.OK) {
      const model = companyConnectedDtoToModelTransformer(response.data)
      return new ApiResult<CompanyConnectedModel[]>(model, false)
    }

    return new ApiResult<CompanyConnectedModel[]>([], true)
  } catch (error) {
    return handleFetchError<CompanyConnectedModel[]>(error)
  }
}

export const fetchCompanyContactData = async (
  companyId: number | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<CompanyContactData>> => {
  if (!companyId || isFlowTheme) {
    return new ApiResult<CompanyContactData>(null, true, new Error('No company ID provided'))
  }

  try {
    const response = await authFetch<CompanyLocationDto>({
      path: `users/companies/${companyId}/location`,
      method: 'GET',
    })
    if (response.status !== StatusCodes.OK) {
      return new ApiResult<CompanyContactData>(null, true)
    }
    const model = fromCompanyDtoToCompanyContactDataModelTransformer(response.data)
    return new ApiResult<CompanyContactData>(model, false)
  } catch (error) {
    return handleFetchError<CompanyContactData>(error)
  }
}

export const deleteCompany = async (companyId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `users/companies/${companyId}`,
    method: 'DELETE',
  })

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

export const generatePartnerNumber = async (companyId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<number>> => {
  const response = await authFetch<number>({
    path: `users/companies/${companyId}/partnerNumber`,
    method: 'PUT',
    payload: {},
  })

  if (response.status === StatusCodes.CREATED) {
    return new ApiResult<number>(response.data, false)
  }

  return new ApiResult<number>(null, true)
}
